import {
    Button, Stack, Card, Collapse, Grid, Typography, Chip, Box,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    CircularProgress,
} from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';
import api from '../Services/Api';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useTranslation } from 'react-i18next';

const transition = 'background-color 200ms ease-out, transform 150ms ease-out, box-shadow 200ms ease-out, border-color 200ms ease-out';
const noMotion = { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } };

const AccountComponent = ({ user, onLogout, disconnectGmail, randomAction }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [expand, setExpand] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [gmailLoading, setGmailLoading] = useState(false);
    const [companyProfile, setCompanyProfile] = useState(null);
    const [writingStyle, setWritingStyle] = useState(null);
    const [expandCompany, setExpandCompany] = useState(false);
    const [expandStyle, setExpandStyle] = useState(false);

    useEffect(() => {
        api.getCompanyProfile().then(({ profile }) => { if (profile) setCompanyProfile(profile); }).catch(() => {});
        api.getWritingStyle().then(({ style }) => { if (style) setWritingStyle(style); }).catch(() => {});
    }, []);

    const accent = theme.palette.secondary?.main || '#76A9AC';

    const logout = useCallback(() => {
        api.logout();
        navigate('/');
        Cookies.remove('token');
        if (onLogout) onLogout();
        window.location.reload();
    }, [onLogout, navigate]);

    const connectGmail = useCallback(() => {
        setGmailLoading(true);
        window.location.replace(`${api.baseURL}/gmail/install`);
    }, []);

    const handleDisconnectGmail = useCallback(async () => {
        setConfirmOpen(false);
        setGmailLoading(true);
        try {
            await disconnectGmail();
        } finally {
            setGmailLoading(false);
        }
    }, [disconnectGmail]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Stack spacing={2} sx={{ padding: '8px 16px', width: '100%' }} alignItems="center">
                {user && (
                    <Stack spacing={0.5} sx={{ width: '100%', textAlign: 'center', pb: 1 }}>
                        <Typography
                            variant="h6"
                            component="h1"
                            sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}
                        >
                            {user.name}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500, fontSize: '0.875rem' }}
                        >
                            {user.domain}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.8125rem' }}
                        >
                            {user.email}
                        </Typography>
                    </Stack>
                )}

                {user && user.total && user.total !== '' && (
                    <Card
                        role="button"
                        tabIndex={0}
                        aria-label={t('viewTimeSaved', 'View time saved details')}
                        aria-expanded={expand}
                        onClick={() => setExpand(!expand)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpand(!expand); } }}
                        sx={{
                            padding: '10px 12px',
                            borderRadius: '14px',
                            width: '100%',
                            maxWidth: 350,
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255, 255, 255, 0.06)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transition,
                            ...noMotion,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderColor: 'rgba(255, 255, 255, 0.18)',
                            },
                            '&:active': { transform: 'scale(0.97)' },
                            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                        }}
                        elevation={0}
                    >
                        <Stack spacing={1} direction="row" alignItems="center" justifyContent="center">
                            <HourglassEmptyRoundedIcon sx={{ color: accent, fontSize: 20 }} />
                            <Typography
                                variant="body2"
                                sx={{ color: '#fff', fontSize: '0.875rem', lineHeight: 1.5, fontWeight: 400, flex: 1 }}
                            >
                                {randomAction}
                            </Typography>
                            <KeyboardArrowDownIcon
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    fontSize: 20,
                                    transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 250ms ease-out',
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        </Stack>
                        <Collapse in={expand} orientation="vertical">
                            <Grid container spacing={1} sx={{ marginTop: '8px' }}>
                                {user.actions && Object.keys(user.actions).map((actionKey) => (
                                    <Grid item xs={6} key={actionKey}>
                                        <Stack spacing={0.5} direction="row" alignItems="baseline" justifyContent="flex-end">
                                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem', fontWeight: 400 }}>
                                                {t(user.actions[actionKey].label)}:
                                            </Typography>
                                            <Typography sx={{ color: accent, fontSize: '0.8125rem', fontWeight: 700 }}>
                                                {user.actions[actionKey].minutes} {t('min')}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Collapse>
                    </Card>
                )}

                {companyProfile && (
                    <Card
                        role="button"
                        tabIndex={0}
                        aria-label={t('companyProfile')}
                        aria-expanded={expandCompany}
                        onClick={() => setExpandCompany(!expandCompany)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandCompany(!expandCompany); } }}
                        data-testid="company-profile-card"
                        sx={{
                            padding: '10px 12px',
                            borderRadius: '14px',
                            width: '100%',
                            maxWidth: 350,
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255, 255, 255, 0.06)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transition,
                            ...noMotion,
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.18)' },
                            '&:active': { transform: 'scale(0.97)' },
                            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                        }}
                        elevation={0}
                    >
                        <Stack spacing={1} direction="row" alignItems="center">
                            <BusinessRoundedIcon sx={{ color: accent, fontSize: 20, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', fontSize: '0.75rem' }}>
                                    {t('companyProfile')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.8125rem', fontWeight: 500 }} noWrap>
                                    {companyProfile.industry || '—'}
                                </Typography>
                            </Box>
                            <KeyboardArrowDownIcon
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: 20,
                                    transform: expandCompany ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 250ms ease-out',
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        </Stack>
                        <Collapse in={expandCompany}>
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8125rem', lineHeight: 1.5, mb: 1 }}>
                                    {companyProfile.summary}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                    {companyProfile.tone && (
                                        <Chip label={companyProfile.tone} size="small" sx={{ backgroundColor: 'rgba(118,169,172,0.15)', color: accent, fontSize: '0.7rem', height: 22 }} />
                                    )}
                                    {companyProfile.size && (
                                        <Chip label={companyProfile.size} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', height: 22 }} />
                                    )}
                                </Stack>
                                {companyProfile.conversationCount > 0 && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', display: 'block', mt: 1 }}>
                                        {companyProfile.conversationCount} {t('configureInAssistant', 'conversations')}
                                    </Typography>
                                )}
                                <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); navigate('/app/property-chat'); }}
                                    sx={{ mt: 0.5, color: accent, fontSize: '0.75rem', textTransform: 'none', p: 0, minHeight: 'auto' }}
                                    aria-label={t('configureInAssistant')}
                                >
                                    {t('configureInAssistant')}
                                </Button>
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {writingStyle && (
                    <Card
                        role="button"
                        tabIndex={0}
                        aria-label={t('writingStyle')}
                        aria-expanded={expandStyle}
                        onClick={() => setExpandStyle(!expandStyle)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandStyle(!expandStyle); } }}
                        data-testid="writing-style-card"
                        sx={{
                            padding: '10px 12px',
                            borderRadius: '14px',
                            width: '100%',
                            maxWidth: 350,
                            cursor: 'pointer',
                            backgroundColor: 'rgba(255, 255, 255, 0.06)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transition,
                            ...noMotion,
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 255, 255, 0.18)' },
                            '&:active': { transform: 'scale(0.97)' },
                            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                        }}
                        elevation={0}
                    >
                        <Stack spacing={1} direction="row" alignItems="center">
                            <EditNoteRoundedIcon sx={{ color: accent, fontSize: 20, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', fontSize: '0.75rem' }}>
                                    {t('writingStyle')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#fff', fontSize: '0.8125rem', fontWeight: 500 }} noWrap>
                                    {t('trainedOnEmails', { count: writingStyle.emailCount || 0 })}
                                </Typography>
                            </Box>
                            <KeyboardArrowDownIcon
                                sx={{
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: 20,
                                    transform: expandStyle ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 250ms ease-out',
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        </Stack>
                        <Collapse in={expandStyle}>
                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8125rem', lineHeight: 1.5, mb: 1 }}>
                                    {writingStyle.summary}
                                </Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                    {writingStyle.greeting && (
                                        <Chip label={writingStyle.greeting} size="small" sx={{ backgroundColor: 'rgba(118,169,172,0.15)', color: accent, fontSize: '0.7rem', height: 22, maxWidth: 160 }} />
                                    )}
                                    {writingStyle.signOff && (
                                        <Chip label={writingStyle.signOff} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', height: 22, maxWidth: 160 }} />
                                    )}
                                    {writingStyle.tone && (
                                        <Chip label={writingStyle.tone} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', height: 22 }} />
                                    )}
                                </Stack>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', display: 'block', mt: 1 }}>
                                    {t('autoUpdates')}
                                </Typography>
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {user.gmail === false && (
                    <Button
                        variant="contained"
                        disabled={gmailLoading}
                        onClick={connectGmail}
                        startIcon={gmailLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
                        sx={{
                            fontSize: '0.875rem',
                            borderRadius: '12px',
                            textTransform: 'none',
                            minHeight: 44,
                            px: 3,
                            transition,
                            ...noMotion,
                            '&:active': { transform: 'scale(0.97)' },
                            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                        }}
                    >
                        {t('connectGmail')}
                    </Button>
                )}

                {user.gmail === true && (
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={gmailLoading}
                        onClick={() => setConfirmOpen(true)}
                        startIcon={gmailLoading ? <CircularProgress size={18} color="inherit" /> : <GoogleIcon />}
                        sx={{
                            fontSize: '0.875rem',
                            borderRadius: '12px',
                            textTransform: 'none',
                            minHeight: 44,
                            px: 3,
                            transition,
                            ...noMotion,
                            '&:active': { transform: 'scale(0.97)' },
                            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                        }}
                    >
                        {t('disconnectGmail')}
                    </Button>
                )}

                <Button
                    variant="text"
                    color="error"
                    onClick={logout}
                    startIcon={<LogoutRoundedIcon />}
                    sx={{
                        fontSize: '0.875rem',
                        borderRadius: '12px',
                        textTransform: 'none',
                        minHeight: 44,
                        px: 3,
                        mt: 1,
                        transition,
                        ...noMotion,
                        '&:active': { transform: 'scale(0.97)' },
                        '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
                    }}
                >
                    {t('logout')}
                </Button>
            </Stack>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(22, 34, 44, 0.98)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        borderRadius: '16px',
                        color: '#fff',
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {t('disconnectGmail')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {t('disconnectGmailConfirm', 'Are you sure you want to disconnect your Gmail account? This action can be reversed later.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setConfirmOpen(false)}
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            minHeight: 44,
                        }}
                    >
                        {t('cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleDisconnectGmail}
                        color="error"
                        variant="contained"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '10px',
                            minHeight: 44,
                        }}
                    >
                        {t('disconnect', 'Disconnect')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AccountComponent;
